import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Download,
  History,
  Power,
  PowerOff,
  Save,
  ShieldPlus,
  TabletSmartphone,
  Trash2,
  Undo2,
  Wrench,
  X,
} from 'lucide-react'
import {
  activateCertificate,
  apiErrorMessage,
  deactivateCertificate,
  fetchCertificatePem,
  fetchComponentMaster,
  fetchDevices,
  fetchLotMaster,
  fetchMachine,
  fetchRevisions,
  fetchTablets,
  restoreRevision,
  updateMachine,
} from '@/services/api'
import { downloadCertPemZip } from '@/services/zip'
import type { Device, IssueDeviceResult } from '@/types/device'
import {
  COMPONENT_CATEGORIES,
  EDITIONS,
  SOFTWARE_VERSION_FIELDS,
  SOFTWARE_VERSION_KEYS,
  componentCategory,
  type ComponentDef,
  type Machine,
  type MachineComponent,
  type MachineInput,
  type MachineRevision,
} from '@/types/machine'
import { isHttpUrl } from '@/lib/utils'
import IssueDeviceDialog from '@/components/IssueDeviceDialog'
import CertDownloadDialog from '@/components/CertDownloadDialog'
import ConfirmDialog from '@/components/ConfirmDialog'
import MarkdownField, { MarkdownView } from '@/components/MarkdownField'

const inputClass =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
const labelClass = 'block text-xs text-gray-600 mb-1'

interface InfoForm {
  serialNumber: string
  deviceName: string
  bodySerial: string
  lot: string
  edition: string
  customer: string
  shippedAt: string
  supportStatus: string
  bodyRecord: string
  bodyInspectionReport: string
  remarks: string
  forkRootRepos: string
  forkMainPcEnv: string
  forkTipSensorFw: string
  vecowDioServer: string
  components: MachineComponent[]
}

function formFromMachine(machine: Machine | undefined): InfoForm {
  return {
    serialNumber: machine?.serialNumber ?? '',
    deviceName: machine?.deviceName ?? '',
    bodySerial: machine?.bodySerial ?? '',
    lot: machine?.lot ?? '',
    edition: machine?.edition ?? '',
    customer: machine?.customer ?? '',
    shippedAt: machine?.shippedAt ?? '',
    supportStatus: machine?.supportStatus ?? '',
    bodyRecord: machine?.bodyRecord ?? '',
    bodyInspectionReport: machine?.bodyInspectionReport ?? '',
    remarks: machine?.remarks ?? '',
    forkRootRepos: machine?.softwareVersions?.forkRootRepos ?? '',
    forkMainPcEnv: machine?.softwareVersions?.forkMainPcEnv ?? '',
    forkTipSensorFw: machine?.softwareVersions?.forkTipSensorFw ?? '',
    vecowDioServer: machine?.softwareVersions?.vecowDioServer ?? '',
    components: (machine?.components ?? []).map((c) => ({ ...c })),
  }
}

function inputFromForm(machine: Machine, form: InfoForm): MachineInput {
  return {
    // 論理削除フラグはフォーム外で管理するため既存値を引き継ぐ
    deletedAt: machine.deletedAt ?? '',
    serialNumber: form.serialNumber,
    deviceName: form.deviceName,
    bodySerial: form.bodySerial,
    lot: form.lot,
    edition: form.edition,
    customer: form.customer,
    shippedAt: form.shippedAt,
    supportStatus: form.supportStatus,
    bodyRecord: form.bodyRecord,
    bodyInspectionReport: form.bodyInspectionReport,
    remarks: form.remarks,
    softwareVersions: {
      forkRootRepos: form.forkRootRepos,
      forkMainPcEnv: form.forkMainPcEnv,
      forkTipSensorFw: form.forkTipSensorFw,
      vecowDioServer: form.vecowDioServer,
    },
    components: form.components.filter((c) => c.name.trim() !== ''),
  }
}

interface CertAction {
  device: Device
  certificateId: string
  action: 'activate' | 'deactivate'
}

// 2つのスナップショット間で変更されたフィールドのラベル一覧を返す
// （保存済みの版どうしの比較と、編集中フォームと保存済み状態の比較で共用する）
type Snapshot = MachineInput

const DIFF_SCALAR_FIELDS: { key: keyof MachineInput; labelKey: string }[] = [
  { key: 'serialNumber', labelKey: 'machines.serial' },
  { key: 'deviceName', labelKey: 'detail.deviceName' },
  { key: 'customer', labelKey: 'machines.customer' },
  { key: 'edition', labelKey: 'machines.edition' },
  { key: 'lot', labelKey: 'detail.lot' },
  { key: 'bodySerial', labelKey: 'machines.bodySerial' },
  { key: 'shippedAt', labelKey: 'machines.shippedAt' },
  { key: 'supportStatus', labelKey: 'machines.supportStatus' },
  { key: 'bodyRecord', labelKey: 'detail.bodyRecord' },
  { key: 'bodyInspectionReport', labelKey: 'detail.bodyInspectionReport' },
  { key: 'remarks', labelKey: 'detail.remarks' },
  { key: 'deletedAt', labelKey: 'detail.deletedBadge' },
]

interface DiffEntry {
  label: string
  // before = この版（古い側）の値 / after = その後の版（新しい側）の値
  before: string
  after: string
}

function componentText(c?: { serialNumber?: string; note?: string }): string {
  if (!c) return ''
  return [c.serialNumber ? `S/N: ${c.serialNumber}` : '', c.note ?? '']
    .filter(Boolean)
    .join('\n')
}

// 差分比較の対象外とする属性（識別子・メタ情報・注記レイヤー）と、個別に比較する属性
const DIFF_IGNORED_KEYS = new Set<string>([
  'machineId',
  'revisionId',
  'createdAt',
  'updatedAt',
  'updatedBy',
  'updateNote',
  'rowColor',
  'cellColors',
  'softwareVersions',
  'components',
  ...DIFF_SCALAR_FIELDS.map((f) => f.key),
])

// 部品マスタにあってこの機材に無い部品を空行として補う（部品管理の一括反映と同じ形）
function mergeComponentsWithMaster(
  components: MachineComponent[],
  master: ComponentDef[]
): MachineComponent[] {
  const names = new Set(components.map((c) => c.name))
  const missing = master
    .filter((def) => !names.has(def.name))
    .map((def) => ({ name: def.name, category: def.category }))
  return missing.length ? [...components, ...missing] : components
}

function diffEntries(
  newer: Snapshot,
  older: Snapshot,
  t: (key: string) => string
): DiffEntry[] {
  const entries: DiffEntry[] = []
  for (const field of DIFF_SCALAR_FIELDS) {
    const before = (older[field.key] as string | undefined) ?? ''
    const after = (newer[field.key] as string | undefined) ?? ''
    if (before !== after) {
      entries.push({ label: t(field.labelKey), before, after })
    }
  }
  for (const key of SOFTWARE_VERSION_KEYS) {
    const before = older.softwareVersions?.[key] ?? ''
    const after = newer.softwareVersions?.[key] ?? ''
    if (before !== after) {
      entries.push({ label: key, before, after })
    }
  }
  const names = new Set([
    ...(newer.components ?? []).map((c) => c.name),
    ...(older.components ?? []).map((c) => c.name),
  ])
  for (const name of names) {
    const a = (newer.components ?? []).find((c) => c.name === name)
    const b = (older.components ?? []).find((c) => c.name === name)
    // 部品の追加・削除（部品管理の一括反映や復元で起きる）は S/N・メモが空でも差分として出す
    if (!a || !b) {
      entries.push({
        label: `${name}${t(a ? 'detail.componentAdded' : 'detail.componentRemoved')}`,
        before: componentText(b),
        after: componentText(a),
      })
      continue
    }
    if (
      (a.serialNumber ?? '') !== (b.serialNumber ?? '') ||
      (a.note ?? '') !== (b.note ?? '')
    ) {
      entries.push({
        label: name,
        before: componentText(b),
        after: componentText(a),
      })
    }
    const categoryA = componentCategory(a)
    const categoryB = componentCategory(b)
    if (categoryA !== categoryB) {
      entries.push({
        label: `${name}${t('detail.componentCategoryChanged')}`,
        before: t(`componentCategory.${categoryB}`),
        after: t(`componentCategory.${categoryA}`),
      })
    }
  }
  // 上記以外の属性（旧台帳由来の項目など）も文字列なら比較し、キー名のまま表示する
  const extraKeys = new Set([...Object.keys(newer), ...Object.keys(older)])
  for (const key of [...extraKeys].sort()) {
    if (DIFF_IGNORED_KEYS.has(key)) continue
    const before = (older as Record<string, unknown>)[key]
    const after = (newer as Record<string, unknown>)[key]
    const beforeText = typeof before === 'string' ? before : ''
    const afterText = typeof after === 'string' ? after : ''
    if (beforeText !== afterText) {
      entries.push({ label: key, before: beforeText, after: afterText })
    }
  }
  return entries
}

export default function MachineDetailPage() {
  const { machineId } = useParams<{ machineId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<InfoForm>(formFromMachine(undefined))
  // 保存時に添える変更メモ（コミットメッセージ相当）。保存ダイアログで入力する
  const [updateNote, setUpdateNote] = useState('')
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [certAction, setCertAction] = useState<CertAction | null>(null)
  const [expandedRevisionId, setExpandedRevisionId] = useState<string | null>(
    null
  )
  const [revisionsOpen, setRevisionsOpen] = useState(false)
  const [issueDialogOpen, setIssueDialogOpen] = useState(false)
  const [issueResult, setIssueResult] = useState<IssueDeviceResult | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['machine', machineId],
    queryFn: () => fetchMachine(machineId!),
    enabled: Boolean(machineId),
    retry: false,
  })
  const machine = data?.machine
  // 編集中フォームと保存済み状態の差分。差分が無ければ保存・元に戻すは押せず、離脱確認も出さない
  // （入力を触っても元の値に戻せば差分なし扱い）
  const pendingChanges = useMemo(
    () =>
      machine ? diffEntries(inputFromForm(machine, form), machine, t) : [],
    [machine, form, t]
  )
  const hasChanges = pendingChanges.length > 0

  const devicesQuery = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
  })
  const lotsQuery = useQuery({
    queryKey: ['lotMaster'],
    queryFn: fetchLotMaster,
  })
  // 構成部品の表示順は部品マスタの並び順に合わせる（マスタ外の部品は末尾）
  const componentMasterQuery = useQuery({
    queryKey: ['componentMaster'],
    queryFn: fetchComponentMaster,
  })
  const componentOrder = useMemo(
    () =>
      new Map(
        (componentMasterQuery.data ?? []).map((c, index) => [c.name, index])
      ),
    [componentMasterQuery.data]
  )
  // この機材に紐付くタブレット（編集はタブレット一覧から行う）
  const tabletsQuery = useQuery({
    queryKey: ['tablets'],
    queryFn: fetchTablets,
  })
  const linkedTablets = (tabletsQuery.data ?? []).filter(
    (tablet) => tablet.linkedMachineId === machineId
  )
  const lotMaster = lotsQuery.data ?? []
  // マスタにない既存のロット値も選択肢に残す（保存でデータを失わないため）
  const lotOptions =
    !form.lot || lotMaster.some((lot) => lot.name === form.lot)
      ? lotMaster.map((lot) => lot.name)
      : [form.lot, ...lotMaster.map((lot) => lot.name)]
  const lotNote = lotMaster.find((lot) => lot.name === form.lot)?.note
  const revisionsQuery = useQuery({
    queryKey: ['revisions', machineId],
    queryFn: () => fetchRevisions(machineId!),
    // 履歴パネルを開いたときだけ取得する
    enabled: Boolean(machineId) && revisionsOpen,
  })
  const revisions = revisionsQuery.data ?? []
  const device = machine?.deviceName
    ? ((devicesQuery.data ?? []).find(
        (d) => d.deviceName === machine.deviceName
      ) ?? null)
    : null

  useEffect(() => {
    setError(null)
  }, [machineId])

  useEffect(() => {
    setForm(formFromMachine(machine))
    setUpdateNote('')
  }, [machine])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['machine', machineId] })
    queryClient.invalidateQueries({ queryKey: ['machines'] })
    queryClient.invalidateQueries({ queryKey: ['revisions', machineId] })
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      updateMachine(machineId!, {
        ...inputFromForm(machine!, form),
        updateNote: updateNote.trim(),
      }),
    onSuccess: () => {
      setError(null)
      setUpdateNote('')
      setSaveDialogOpen(false)
      invalidate()
    },
    onError: (err) => {
      // エラーはページ上部のバナーで表示する（メモは保持され、再保存でそのまま使える）
      setSaveDialogOpen(false)
      setError(apiErrorMessage(err) ?? t('detail.saveError'))
    },
  })

  // 論理削除・復元（保存済みの状態に deletedAt だけ設定/クリアして上書き）
  const deleteMutation = useMutation({
    mutationFn: (deletedAt: string) =>
      updateMachine(machineId!, {
        ...inputFromForm(machine!, formFromMachine(machine!)),
        deletedAt,
      }),
    onSuccess: () => {
      setError(null)
      invalidate()
    },
    onError: (err) => setError(apiErrorMessage(err) ?? t('detail.saveError')),
  })

  const handleDelete = () => {
    if (hasChanges && !window.confirm(t('detail.confirmLeave'))) return
    if (!window.confirm(t('detail.deleteConfirm'))) return
    deleteMutation.mutate(new Date().toISOString())
  }

  const handleRestoreDeleted = () => {
    if (hasChanges && !window.confirm(t('detail.confirmLeave'))) return
    deleteMutation.mutate('')
  }

  const restoreMutation = useMutation({
    mutationFn: (revisionId: string) => restoreRevision(machineId!, revisionId),
    onSuccess: () => {
      setError(null)
      invalidate()
    },
    onError: (err) =>
      setError(apiErrorMessage(err) ?? t('detail.restoreError')),
  })

  const handleRestore = (revisionId: string) => {
    if (hasChanges && !window.confirm(t('detail.confirmLeave'))) return
    if (!window.confirm(t('detail.restoreConfirm'))) return
    restoreMutation.mutate(revisionId)
  }

  const certMutation = useMutation({
    mutationFn: async (action: CertAction) => {
      if (action.action === 'deactivate') {
        await deactivateCertificate(action.certificateId)
      } else {
        await activateCertificate(action.certificateId)
      }
    },
    onSuccess: () => {
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    },
    onError: (err) => {
      setError(apiErrorMessage(err) ?? t('devices.updateError'))
    },
    onSettled: () => {
      setCertAction(null)
    },
  })

  // 証明書一式の再ダウンロード（SSMに保管済みの秘密鍵があれば含まれる）
  const downloadCertMutation = useMutation({
    mutationFn: async (certificateId: string) => {
      const result = await fetchCertificatePem(certificateId)
      await downloadCertPemZip(device!, result)
    },
    onSuccess: () => setError(null),
    onError: (err) =>
      setError(apiErrorMessage(err) ?? t('devices.downloadError')),
  })

  const set = <K extends keyof InfoForm>(key: K, value: InfoForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  // 表示用の構成部品。マスタにあって機材に無い部品は空行として出す（復元で消えた部品の再入力用）
  const componentMaster = componentMasterQuery.data
  const displayComponents = useMemo(
    () =>
      componentMaster
        ? mergeComponentsWithMaster(form.components, componentMaster)
        : form.components,
    [form.components, componentMaster]
  )

  // index は displayComponents 上の位置。補われた空行を編集したときはフォームにも取り込む
  const setComponent = (
    index: number,
    key: keyof MachineComponent,
    value: string
  ) => {
    setForm((prev) => {
      const base = componentMaster
        ? mergeComponentsWithMaster(prev.components, componentMaster)
        : prev.components
      const components = base.map((c, i) =>
        i === index ? { ...c, [key]: value } : c
      )
      return { ...prev, components }
    })
  }

  const handleBack = () => {
    if (hasChanges && !window.confirm(t('detail.confirmLeave'))) return
    navigate('/')
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <p className="text-sm text-gray-600">{t('detail.notFound')}</p>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
      </div>
    )
  }

  if (isLoading || !machine) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-500">{t('common.loading')}</span>
      </div>
    )
  }

  return (
    <div>
      {/* Header（アプリヘッダー(h-16)の直下に固定表示） */}
      <div className="sticky top-16 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 -mt-8 pt-6 mb-6 bg-gray-50/95 backdrop-blur border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </button>
          <div>
            <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900">
              {machine.serialNumber ||
                machine.deviceName ||
                t('detail.noSerial')}
              {machine.deletedAt && (
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  {t('detail.deletedBadge')}
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {machine.customer || '-'} / {machine.edition || '-'} /{' '}
              {machine.deviceName || t('detail.noDeviceName')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              // 保存していない変更を破棄して、最後に保存された状態へ戻す
              if (!window.confirm(t('detail.undoConfirm'))) return
              setForm(formFromMachine(machine))
              setError(null)
            }}
            disabled={!hasChanges}
            title={t('detail.undoHint')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Undo2 className="h-4 w-4" />
            {t('detail.undo')}
          </button>
          <button
            onClick={() => setSaveDialogOpen(true)}
            disabled={!hasChanges || saveMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? t('detail.saving') : t('detail.save')}
          </button>
          <button
            onClick={() => setRevisionsOpen(true)}
            title={t('detail.revisionsTitle')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <History className="h-4 w-4" />
            {t('detail.revisionsButton')}
          </button>
          {machine.deletedAt ? (
            <button
              onClick={handleRestoreDeleted}
              disabled={deleteMutation.isPending}
              title={t('detail.restoreDeletedHint')}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50 disabled:opacity-50 transition-colors"
            >
              <Undo2 className="h-4 w-4" />
              {t('detail.restoreDeleted')}
            </button>
          ) : (
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              title={t('detail.deleteHint')}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-700 bg-white border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              {t('detail.delete')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* 基本情報 */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            {t('detail.infoTitle')}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className={labelClass}>{t('machines.serial')}</label>
              <input
                type="text"
                value={form.serialNumber}
                onChange={(e) => set('serialNumber', e.target.value)}
                placeholder={t('detail.serialPlaceholder')}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>{t('machines.customer')}</label>
              <input
                type="text"
                value={form.customer}
                onChange={(e) => set('customer', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>{t('machines.edition')}</label>
              <select
                value={form.edition}
                onChange={(e) => set('edition', e.target.value)}
                className={inputClass}
              >
                <option value="">-</option>
                {EDITIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className={labelClass}>{t('detail.lot')}</label>
                <Link
                  to="/lots"
                  className="text-xs text-primary-600 hover:underline"
                  title={t('detail.lotManageHint')}
                >
                  {t('nav.lots')}
                </Link>
              </div>
              <select
                value={form.lot}
                onChange={(e) => set('lot', e.target.value)}
                className={inputClass}
              >
                <option value="">-</option>
                {lotOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              {lotNote && (
                <MarkdownView
                  value={lotNote}
                  className="mt-1 text-xs text-gray-500"
                />
              )}
            </div>
            <div>
              <label className={labelClass}>{t('detail.deviceName')}</label>
              <input
                type="text"
                value={form.deviceName}
                onChange={(e) => set('deviceName', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>{t('machines.bodySerial')}</label>
              <input
                type="text"
                value={form.bodySerial}
                onChange={(e) => set('bodySerial', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>{t('machines.shippedAt')}</label>
              <input
                type="text"
                value={form.shippedAt}
                onChange={(e) => set('shippedAt', e.target.value)}
                placeholder="26/1/15"
                className={inputClass}
              />
            </div>
            {/* 車体出荷写真・車体検査成績書へのリンク（URL） */}
            {(
              [
                ['bodyRecord', 'detail.bodyRecord', 'detail.bodyRecordHint'],
                [
                  'bodyInspectionReport',
                  'detail.bodyInspectionReport',
                  'detail.bodyInspectionReportHint',
                ],
              ] as const
            ).map(([key, labelKey, hintKey]) => (
              <div key={key}>
                <div className="flex items-baseline justify-between">
                  <label className={labelClass}>{t(labelKey)}</label>
                  {isHttpUrl(form[key]) && (
                    <a
                      href={form[key]}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-primary-600 hover:underline"
                    >
                      {t('detail.openLink')}
                    </a>
                  )}
                </div>
                <input
                  type="url"
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  placeholder={t(hintKey)}
                  className={inputClass}
                />
              </div>
            ))}
            <div className="col-span-2">
              <label className={labelClass}>
                {t('machines.supportStatus')}
              </label>
              <MarkdownField
                value={form.supportStatus}
                onChange={(value) => set('supportStatus', value)}
                placeholder={t('detail.markdownHint')}
              />
            </div>
            <div className="col-span-2 sm:col-span-4">
              <label className={labelClass}>{t('detail.remarks')}</label>
              <MarkdownField
                value={form.remarks}
                onChange={(value) => set('remarks', value)}
                placeholder={t('detail.markdownHint')}
              />
            </div>
          </div>
          {machine.updatedAt && (
            <p className="mt-3 text-xs text-gray-400">
              {t('detail.lastUpdated', {
                date: new Date(machine.updatedAt).toLocaleString(),
                by: machine.updatedBy ?? '-',
              })}
            </p>
          )}
          {/* 最新の保存に添えられた変更メモ（Markdown可） */}
          {machine.updateNote && (
            <div className="mt-1 flex items-start gap-1 text-xs text-gray-500">
              <span className="shrink-0">{t('detail.updateNoteShort')}:</span>
              <MarkdownView
                value={machine.updateNote}
                className="text-xs text-gray-500"
              />
            </div>
          )}
        </section>

        {/* ソフトバージョン */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            {t('detail.softwareTitle')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {SOFTWARE_VERSION_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <label className={labelClass}>{label}</label>
                <input
                  type="text"
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  placeholder="V1.0.0"
                  className={inputClass}
                />
              </div>
            ))}
          </div>
        </section>

        {/* 構成部品（部品の追加・削除は部品管理画面から全機材へ一括反映する） */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('detail.componentsTitle')}
            </h3>
            <Link
              to="/components"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              title={t('detail.componentsManageHint')}
            >
              <Wrench className="h-3.5 w-3.5" />
              {t('nav.components')}
            </Link>
          </div>
          <div className="space-y-6">
            {COMPONENT_CATEGORIES.map((category) => (
              <div key={category}>
                <h4 className="text-xs font-semibold text-gray-700 mb-2">
                  {t(`componentCategory.${category}`)}
                </h4>
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-52">
                          {t('detail.componentName')}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-64">
                          {t('detail.componentSerial')}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          {t('detail.componentNote')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {displayComponents
                        .map((component, index) => ({ component, index }))
                        .filter(
                          ({ component }) =>
                            componentCategory(component) === category
                        )
                        .sort(
                          (a, b) =>
                            (componentOrder.get(a.component.name) ??
                              componentOrder.size + a.index) -
                            (componentOrder.get(b.component.name) ??
                              componentOrder.size + b.index)
                        )
                        .map(({ component, index }) => (
                          <tr key={index}>
                            <td className="px-3 py-1.5 text-sm text-gray-900">
                              {component.name}
                            </td>
                            <td className="px-2 py-1.5 align-top">
                              <input
                                type="text"
                                value={component.serialNumber ?? ''}
                                onChange={(e) =>
                                  setComponent(
                                    index,
                                    'serialNumber',
                                    e.target.value
                                  )
                                }
                                className="w-full px-2 py-1.5 text-sm font-mono border border-transparent rounded hover:border-gray-200 focus:border-gray-300 focus:outline-none"
                              />
                            </td>
                            <td className="px-2 py-1.5 align-top">
                              <MarkdownField
                                value={component.note ?? ''}
                                onChange={(value) =>
                                  setComponent(index, 'note', value)
                                }
                                minRows={1}
                                borderless
                              />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 証明書（無効化は誤操作防止のためここに配置） */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            {t('detail.certTitle')}
          </h3>
          {device && device.certificates.length > 0 ? (
            <ul className="space-y-2 max-w-2xl">
              {device.certificates.map((certificate) => (
                <li
                  key={certificate.certificateId}
                  className="flex items-center justify-between gap-3 px-4 py-3 border border-gray-200 rounded-md"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          certificate.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {certificate.status}
                      </span>
                      <span
                        className="text-xs text-gray-500 font-mono truncate"
                        title={certificate.certificateId}
                      >
                        {certificate.certificateId.slice(0, 16)}…
                      </span>
                    </div>
                    {certificate.creationDate && (
                      <p className="mt-1 text-xs text-gray-400">
                        {t('devices.created')}:{' '}
                        {new Date(
                          certificate.creationDate
                        ).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() =>
                        downloadCertMutation.mutate(certificate.certificateId)
                      }
                      disabled={downloadCertMutation.isPending}
                      title={t('devices.downloadHint')}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <Download className="h-3.5 w-3.5" />
                      {t('devices.download')}
                    </button>
                    {certificate.status === 'ACTIVE' ? (
                      <button
                        onClick={() =>
                          setCertAction({
                            device,
                            certificateId: certificate.certificateId,
                            action: 'deactivate',
                          })
                        }
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-red-700 bg-white border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                      >
                        <PowerOff className="h-3.5 w-3.5" />
                        {t('devices.deactivate')}
                      </button>
                    ) : (
                      <button
                        onClick={() =>
                          setCertAction({
                            device,
                            certificateId: certificate.certificateId,
                            action: 'activate',
                          })
                        }
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-green-700 bg-white border border-green-200 rounded-md hover:bg-green-50 transition-colors"
                      >
                        <Power className="h-3.5 w-3.5" />
                        {t('devices.activate')}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : machine.deviceName ? (
            <button
              onClick={() => setIssueDialogOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
            >
              <ShieldPlus className="h-4 w-4" />
              {t('detail.issueCert', { deviceName: machine.deviceName })}
            </button>
          ) : (
            <p className="text-sm text-gray-500">
              {t('detail.noDeviceNameHint')}
            </p>
          )}
        </section>

        {/* 紐付きタブレット（登録・紐付けの変更はタブレット一覧から行う） */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            {t('detail.tabletsTitle')}
          </h3>
          {linkedTablets.length > 0 ? (
            <ul className="space-y-2 max-w-2xl">
              {linkedTablets.map((tablet) => (
                <li key={tablet.tabletId}>
                  <Link
                    to="/tablets"
                    className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <TabletSmartphone className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-900">
                      {tablet.name || tablet.model || '-'}
                    </span>
                    {tablet.serialNumber && (
                      <span className="text-xs text-gray-500 font-mono">
                        S/N: {tablet.serialNumber}
                      </span>
                    )}
                    {tablet.imei && (
                      <span className="text-xs text-gray-500 font-mono">
                        IMEI: {tablet.imei}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">{t('detail.noTablets')}</p>
          )}
        </section>
      </div>

      {/* 保存確認ダイアログ（差分を確認して変更メモを添える） */}
      {saveDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Save className="h-5 w-5 text-gray-400" />
                {t('detail.saveDialogTitle')}
              </h2>
              <button
                onClick={() => setSaveDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto space-y-3">
              {pendingChanges.length === 0 ? (
                <p className="text-sm text-gray-400">{t('detail.noDiff')}</p>
              ) : (
                pendingChanges.map((entry) => (
                  <div key={entry.label}>
                    <p className="text-xs font-medium text-gray-500 mb-1">
                      {entry.label}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                        <p className="text-[10px] font-medium text-red-400 mb-0.5">
                          {t('detail.saveDialogBefore')}
                        </p>
                        <p className="text-xs text-red-900 whitespace-pre-wrap break-words">
                          {entry.before || '-'}
                        </p>
                      </div>
                      <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2">
                        <p className="text-[10px] font-medium text-green-500 mb-0.5">
                          {t('detail.saveDialogAfter')}
                        </p>
                        <p className="text-xs text-green-900 whitespace-pre-wrap break-words">
                          {entry.after || '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="px-6 py-4 border-t">
              <label className={labelClass}>
                {t('detail.updateNoteLabel')}
              </label>
              <MarkdownField
                value={updateNote}
                onChange={setUpdateNote}
                placeholder={t('detail.updateNoteHint')}
              />
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setSaveDialogOpen(false)}
                disabled={saveMutation.isPending}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => saveMutation.mutate()}
                // 差分が無い保存は版を増やすだけなので受け付けない（メモだけの保存も不可）
                disabled={saveMutation.isPending || pendingChanges.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="h-4 w-4" />
                {saveMutation.isPending ? t('detail.saving') : t('detail.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 変更履歴モーダル */}
      {revisionsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <History className="h-5 w-5 text-gray-400" />
                {t('detail.revisionsTitle')}
              </h2>
              <button
                onClick={() => setRevisionsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto">
              {revisionsQuery.isLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              ) : revisions.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-400">
                  {t('detail.noRevisions')}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                  {revisions.map((revision, index) => {
                    // 各行は1回の保存を表す。版はその保存で上書きされた直前の状態なので、
                    // 保存の作成者・時刻は次に新しい状態（先頭なら現在）から取り、
                    // 差分はこの版→次の状態、メモはこの版に付いた「上書きした保存のメモ」を出す
                    const newer: Machine | MachineRevision =
                      index === 0 ? machine : revisions[index - 1]
                    const changed = diffEntries(newer, revision, t)
                    const expanded = expandedRevisionId === revision.revisionId
                    return (
                      <li key={revision.revisionId}>
                        <div
                          onClick={() =>
                            setExpandedRevisionId(
                              expanded ? null : revision.revisionId
                            )
                          }
                          className="flex items-start justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap text-sm">
                              {expanded ? (
                                <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                              )}
                              <span className="text-gray-900">
                                {newer.updatedAt
                                  ? new Date(newer.updatedAt).toLocaleString()
                                  : '-'}
                              </span>
                              <span className="text-gray-500">
                                {newer.updatedBy ?? '-'}
                              </span>
                              {index === 0 && (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                                  {t('detail.previousVersion')}
                                </span>
                              )}
                            </div>
                            {/* この版を保存したときの変更メモ（Markdown可） */}
                            {revision.updateNote && (
                              <MarkdownView
                                value={revision.updateNote}
                                className="mt-1 ml-6 text-gray-700 break-words"
                              />
                            )}
                            <div className="mt-1 ml-6 flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs text-gray-400">
                                {t('detail.changedFields')}:
                              </span>
                              {changed.length === 0 ? (
                                <span className="text-xs text-gray-400">-</span>
                              ) : (
                                changed.map((entry) => (
                                  <span
                                    key={entry.label}
                                    className="inline-flex px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700"
                                  >
                                    {entry.label}
                                  </span>
                                ))
                              )}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRestore(revision.revisionId)
                            }}
                            disabled={restoreMutation.isPending}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors shrink-0"
                          >
                            <Undo2 className="h-3.5 w-3.5" />
                            {t('detail.restore')}
                          </button>
                        </div>

                        {/* 差分の詳細（クリックで展開） */}
                        {expanded && (
                          <div className="px-4 pb-4 ml-6 space-y-3">
                            {changed.length === 0 ? (
                              <p className="text-xs text-gray-400">
                                {t('detail.noDiff')}
                              </p>
                            ) : (
                              changed.map((entry) => (
                                <div key={entry.label}>
                                  <p className="text-xs font-medium text-gray-500 mb-1">
                                    {entry.label}
                                  </p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                                      <p className="text-[10px] font-medium text-red-400 mb-0.5">
                                        {t('detail.diffBefore')}
                                      </p>
                                      <p className="text-xs text-red-900 whitespace-pre-wrap break-words">
                                        {entry.before || '-'}
                                      </p>
                                    </div>
                                    <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2">
                                      <p className="text-[10px] font-medium text-green-500 mb-0.5">
                                        {t('detail.diffAfter')}
                                      </p>
                                      <p className="text-xs text-green-900 whitespace-pre-wrap break-words">
                                        {entry.after || '-'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
            <div className="flex justify-end px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setRevisionsOpen(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <IssueDeviceDialog
        open={issueDialogOpen}
        siteIds={[
          ...new Set(
            (devicesQuery.data ?? [])
              .map((d) => d.siteId)
              .filter((s): s is string => Boolean(s))
          ),
        ].sort()}
        fixedDeviceName={machine.deviceName ?? null}
        onClose={() => setIssueDialogOpen(false)}
        onIssued={(result) => {
          setIssueDialogOpen(false)
          setIssueResult(result)
          queryClient.invalidateQueries({ queryKey: ['devices'] })
        }}
      />

      <CertDownloadDialog
        result={issueResult}
        onClose={() => setIssueResult(null)}
      />

      <ConfirmDialog
        open={certAction !== null}
        title={
          certAction?.action === 'deactivate'
            ? t('confirmDialog.deactivateTitle')
            : t('confirmDialog.activateTitle')
        }
        message={
          certAction?.action === 'deactivate'
            ? t('confirmDialog.deactivateMessage', {
                deviceName: certAction?.device.deviceName,
              })
            : t('confirmDialog.activateMessage', {
                deviceName: certAction?.device.deviceName,
              })
        }
        confirmLabel={
          certAction?.action === 'deactivate'
            ? t('devices.deactivate')
            : t('devices.activate')
        }
        danger={certAction?.action === 'deactivate'}
        busy={certMutation.isPending}
        onClose={() => setCertAction(null)}
        onConfirm={() => certAction && certMutation.mutate(certAction)}
      />
    </div>
  )
}
